import { Box, Divider, Group, ScrollArea, Stack, Text } from "@mantine/core"
import { useTranslation } from "react-i18next"

import type { EcoModulationImportValidationErrorDto } from "~/gen/api"

type EcoModulationImportResultSummaryProps = {
  readonly productsImported: number | null
  readonly errors: readonly EcoModulationImportValidationErrorDto[]
}

const StatusDot = ({ tone }: { readonly tone: "valid" | "error" }) => {
  return (
    <Box
      w={8}
      h={8}
      bdrs="50%"
      bg={
        tone === "valid"
          ? "var(--mantine-color-success)"
          : "var(--mantine-color-error-text)"
      }
      style={{ flexShrink: 0 }}
      aria-hidden
    />
  )
}

export const EcoModulationImportResultSummary = ({
  productsImported,
  errors,
}: EcoModulationImportResultSummaryProps) => {
  const { t } = useTranslation("dataTransfer")
  const hasErrors = errors.length > 0
  const errorColor = hasErrors ? "var(--mantine-color-error-text)" : undefined

  return (
    <Stack
      gap="var(--tfg-molecule-padding-y)"
      p="var(--tfg-molecule-padding-y) var(--tfg-molecule-padding-x)"
      bd="1px solid var(--mantine-color-gray-2)"
      bdrs="var(--tfg-card-border-radius)"
      bg="var(--mantine-color-white)"
      w="100%"
      miw={0}
    >
      <Text size="sm" fw={600}>
        {t("import.ecoModulation.summaryModal.importResultTitle")}
      </Text>

      <Divider color="var(--mantine-color-gray-2)" />

      <Group justify="space-between" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <StatusDot tone="valid" />
          <Text size="sm">
            {t("import.ecoModulation.summaryModal.productsImported")}
          </Text>
        </Group>
        <Text size="sm">
          {productsImported === null ? "—" : productsImported}
        </Text>
      </Group>

      <Group wrap="nowrap" align="flex-start" gap="xs">
        <Box mt={6}>
          <StatusDot tone="error" />
        </Box>
        <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
          <Group justify="space-between" wrap="nowrap">
            <Text size="sm" c={errorColor}>
              {t("import.ecoModulation.summaryModal.importErrors")}
            </Text>
            <Text size="sm" c={errorColor}>
              {errors.length}
            </Text>
          </Group>
          {hasErrors && (
            <ScrollArea.Autosize mah={176} type="hover">
              <Stack gap={4}>
                {errors.map((error, index) => (
                  <Group
                    key={`${error.rowNumber}-${error.message}-${index}`}
                    wrap="nowrap"
                    align="flex-start"
                    gap="xs"
                  >
                    <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                      ↳
                    </Text>
                    {error.rowNumber > 0 && (
                      <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                        {t("import.ecoModulation.summaryModal.lineNumber", {
                          rowNumber: error.rowNumber,
                        })}
                      </Text>
                    )}
                    <Text size="xs">{error.message}</Text>
                  </Group>
                ))}
              </Stack>
            </ScrollArea.Autosize>
          )}
        </Stack>
      </Group>
    </Stack>
  )
}
